import { useEffect, useState } from "react";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter, HStack, VStack } from "@astryxdesign/core/Layout";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { TextInput } from "@astryxdesign/core/TextInput";
import { Button } from "@astryxdesign/core/Button";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Divider } from "@astryxdesign/core/Divider";
import { Token } from "@astryxdesign/core/Token";
import { Banner } from "@astryxdesign/core/Banner";
import { useToast } from "@astryxdesign/core/Toast";
import { api } from "../../lib/api";
import { useAuth } from "./AuthProvider";

interface AccountDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

// Self-service only: every field here edits the signed-in user's own
// account, taken from useAuth() — there is no id parameter anywhere in this
// component for a caller to redirect at someone else's account. Editing
// anyone else goes through UsersPage, which is admin-gated.
export function AccountDialog({ isOpen, onOpenChange }: AccountDialogProps) {
  const { user, setUser } = useAuth();
  const showToast = useToast();

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [emailFieldError, setEmailFieldError] = useState<string | null>(null);
  const [profileRequestError, setProfileRequestError] = useState<string | null>(null);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordFieldError, setNewPasswordFieldError] = useState<string | null>(null);
  const [passwordRequestError, setPasswordRequestError] = useState<string | null>(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    if (!isOpen || !user) return;
    setDisplayName(user.display_name);
    setEmail(user.email);
    setEmailFieldError(null);
    setProfileRequestError(null);
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordFieldError(null);
    setPasswordRequestError(null);
  }, [isOpen, user]);

  if (!user) return null;

  const saveProfile = async () => {
    setProfileRequestError(null);
    if (!email.trim()) {
      setEmailFieldError("Email is required.");
      return;
    }
    setEmailFieldError(null);
    setIsSavingProfile(true);
    try {
      const updated = await api.auth.updateProfile({
        display_name: displayName.trim(),
        email: email.trim(),
        version: user.version,
      });
      setUser(updated);
      showToast({ body: "Profile updated" });
    } catch (err) {
      setProfileRequestError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsSavingProfile(false);
    }
  };

  const changePassword = async () => {
    setPasswordRequestError(null);
    if (!newPassword.trim()) {
      setNewPasswordFieldError("New password is required.");
      return;
    }
    setNewPasswordFieldError(null);
    setIsSavingPassword(true);
    try {
      await api.auth.changePassword(currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      showToast({ body: "Password changed" });
    } catch (err) {
      setPasswordRequestError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose="form" width={440}>
      <Layout
        header={
          <DialogHeader
            title="My account"
            subtitle={user.username}
            endContent={<Token color={user.role.permissions.includes("roles.manage") ? "purple" : "default"} label={user.role.name} size="sm" />}
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <VStack gap={5}>
              <VStack gap={3}>
                <Heading level={3}>Profile</Heading>
                <FormLayout>
                  <TextInput
                    label="Display name"
                    value={displayName}
                    onChange={setDisplayName}
                    onEnter={() => {
                      if (!isSavingProfile) void saveProfile();
                    }}
                  />
                  <TextInput
                    label="Email"
                    value={email}
                    onChange={setEmail}
                    status={emailFieldError ? { type: "error", message: emailFieldError } : undefined}
                    isRequired
                    onEnter={() => {
                      if (!isSavingProfile) void saveProfile();
                    }}
                  />
                  {profileRequestError && (
                    <Banner status="error" title="Couldn't save profile" description={profileRequestError} />
                  )}
                </FormLayout>
                <HStack justify="end">
                  <Button label="Save profile" variant="secondary" onClick={saveProfile} isLoading={isSavingProfile} />
                </HStack>
              </VStack>

              <Divider />

              {user.auth_provider === "local" ? (
                <VStack gap={3}>
                  <Heading level={3}>Change password</Heading>
                  <FormLayout>
                    <TextInput
                      label="Current password"
                      type="password"
                      value={currentPassword}
                      onChange={setCurrentPassword}
                      isRequired
                      onEnter={() => {
                        if (!isSavingPassword) void changePassword();
                      }}
                    />
                    <TextInput
                      label="New password"
                      type="password"
                      value={newPassword}
                      onChange={setNewPassword}
                      status={newPasswordFieldError ? { type: "error", message: newPasswordFieldError } : undefined}
                      isRequired
                      onEnter={() => {
                        if (!isSavingPassword) void changePassword();
                      }}
                    />
                    {passwordRequestError && (
                      <Banner status="error" title="Couldn't change password" description={passwordRequestError} />
                    )}
                  </FormLayout>
                  <HStack justify="end">
                    <Button
                      label="Update password"
                      variant="secondary"
                      onClick={changePassword}
                      isLoading={isSavingPassword}
                    />
                  </HStack>
                </VStack>
              ) : (
                <Text type="supporting" color="secondary">
                  This account signs in via SSO — password is managed by your identity provider.
                </Text>
              )}
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack hAlign="end">
              <Button
                label="Close"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                isDisabled={isSavingProfile || isSavingPassword}
              />
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
